import { Injectable } from '@angular/core';

/**
 * Énumération des niveaux de log disponibles
 */
export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3
}

/**
 * Interface de configuration du Logger
 */
export interface LoggerConfig {
    /** Activer ou désactiver le logger */
    enabled: boolean;
    /** Niveau minimum de log à afficher */
    minLevel: LogLevel;
    /** Activer l'affichage de la stack trace */
    enableStackTrace: boolean;
    /** Préfixe pour identifier les logs */
    prefix?: string;
    /** Format de timestamp à utiliser */
    timestampFormat?: string;
}

/**
 * Service Logger centralisé pour la gestion des logs côté client
 * 
 * Ce service fournit un système de logging centralisé avec les fonctionnalités suivantes :
 * - Gestion des niveaux de log (DEBUG, INFO, WARN, ERROR)
 * - Préservation de la stack trace
 * - Configuration centralisée des niveaux
 * - Formatage cohérent avec timestamp
 * - Méthodes statiques pour faciliter l'utilisation
 * 
 * @example\n * LoggerService.debug('Message de debug');\n * LoggerService.info('Information importante');\n * LoggerService.warn('Attention, problème détecté');\n * LoggerService.error('Erreur critique', errorObject);\n */
@Injectable({
    providedIn: 'root'
})
export class LoggerService {

    /** Configuration statique du Logger */
    private static config: LoggerConfig = {
        enabled: true,
        minLevel: LogLevel.WARN,
        enableStackTrace: true,
        prefix: 'KUERLOG',
        timestampFormat: 'dd/MM/yyyy HH:mm:ss'
    };

    /**
     * Constructeur du service Logger
     * 
     * @param config Configuration optionnelle pour initialiser le logger
     */
    constructor() {
        // Initialisation silencieuse
    }

    /**
     * Méthode interne pour formater un message de log
     * 
     * @param level Niveau de log
     * @param message Message à formater
     * @param args Arguments supplémentaires
     * @param stackTrace Stack trace optionnelle
     * @returns Message formaté
     */
    private static formatMessage(
        level: LogLevel,
        message: string,
        args: any[] = [],
        stackTrace?: string
    ): string {
        if (!this.config.enabled) {
            return '';
        }

        // Vérifier si le niveau est suffisamment élevé
        if (level < this.config.minLevel) {
            return '';
        }

        // Formater le timestamp
        const timestamp = new Date().toLocaleString('fr-FR', {
            timeZone: 'Europe/Paris',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });

        // Formater le niveau
        const levelNames = ['DEBUG', 'INFO', 'WARN', 'ERROR'];
        const levelName = levelNames[level] || 'UNKNOWN';

        // Construire le préfixe
        const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';

        // Construire le message principal
        const mainMessage = `[${timestamp}] ${prefix}[${levelName}] ${message}`;

        // Ajouter les arguments s'il y en a
        const argsMessage = args.length > 0 ? ` ${JSON.stringify(args, null, 2)}` : '';

        // Ajouter la stack trace si configurée
        const stackMessage = stackTrace && this.config.enableStackTrace
            ? `\nStack Trace:\n${stackTrace}`
            : '';

        return `${mainMessage}${argsMessage}${stackMessage}`;
    }

    /**
     * Méthode interne pour obtenir la stack trace
     * 
     * @returns Stack trace de l'appelant (sans les méthodes du Logger)
     */
    private static getCallerStackTrace(): string | undefined {
        if (!this.config.enableStackTrace) {
            return undefined;
        }

        try {
            const error = new Error();
            const stack = error.stack;

            if (!stack) {
                return undefined;
            }

            // Diviser la stack trace en lignes
            const lines = stack.split('\n');

            // Trouver l'indice de la première ligne qui n'est pas liée au Logger
            let callerIndex = -1;
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                // Ignorer les lignes liées au Logger
                if (!line.includes('LoggerService.') &&
                    !line.includes('getCallerStackTrace') &&
                    !line.includes('formatMessage')) {
                    callerIndex = i;
                    break;
                }
            }

            // Si on a trouvé l'appelant, retourner les lignes à partir de là
            if (callerIndex > -1) {
                return lines.slice(callerIndex).join('\n');
            }

            return stack;
        } catch {
            return undefined;
        }
    }

    /**
     * Méthode interne pour logger un message
     *
     * @param level Niveau de log
     * @param message Message à logger
     * @param args Arguments supplémentaires
     */
    private static internalLog(level: LogLevel, message: string, ...args: any[]): void {
        if (!this.config.enabled || level < this.config.minLevel) {
            return;
        }

        const stackTrace = this.getCallerStackTrace();
        const formattedMessage = this.formatMessage(level, message, args, stackTrace);

        if (formattedMessage) {
            // Utiliser la méthode console appropriée selon le niveau
            switch (level) {
                case LogLevel.DEBUG:
                    console.debug(formattedMessage);
                    break;
                case LogLevel.INFO:
                    console.info(formattedMessage);
                    break;
                case LogLevel.WARN:
                    console.warn(formattedMessage);
                    break;
                case LogLevel.ERROR:
                    console.error(formattedMessage);
                    break;
                default:
                    console.log(formattedMessage);
            }
        }
    }

    // ==========================================================================
    // MÉTHODES PUBLIQUES STATIQUES POUR L'UTILISATION
    // ==========================================================================

    /**
     * Logger un message de niveau DEBUG
     *
     * @param message Message à logger
     * @param args Arguments supplémentaires à afficher
     *
     * @example\n * LoggerService.debug('Variable X = ', someValue);\n */
    static debug(message: string, ...args: any[]): void {
        this.internalLog(LogLevel.DEBUG, message, ...args);
    }

    /**
     * Logger un message de niveau INFO
     *
     * @param message Message à logger
     * @param args Arguments supplémentaires à afficher
     *
     * @example\n * LoggerService.info('Utilisateur connecté:', userId);\n */
    static info(message: string, ...args: any[]): void {
        this.internalLog(LogLevel.INFO, message, ...args);
    }

    /**
     * Logger un message de niveau WARN
     *
     * @param message Message à logger
     * @param args Arguments supplémentaires à afficher
     *
     * @example\n * LoggerService.warn('Paramètre obsolète utilisé:', oldParam);\n */
    static warn(message: string, ...args: any[]): void {
        this.internalLog(LogLevel.WARN, message, ...args);
    }

    /**
     * Logger un message de niveau ERROR
     *
     * @param message Message à logger
     * @param args Arguments supplémentaires à afficher
     *
     * @example\n * LoggerService.error('Erreur lors de l\\'opération:', error);\n */
    static error(message: string, ...args: any[]): void {
        this.internalLog(LogLevel.ERROR, message, ...args);
    }

    /**
     * Logger un message générique (alias pour info)
     *
     * @param message Message à logger
     * @param args Arguments supplémentaires à afficher
     */
    static log(message: string, ...args: any[]): void {
        this.internalLog(LogLevel.INFO, message, ...args);
    }

    // ==========================================================================
    // MÉTHODES DE CONFIGURATION
    // ==========================================================================

    /**
     * Configure le Logger avec de nouveaux paramètres
     * 
     * @param newConfig Configuration partielle à appliquer
     * 
     * @example\n * LoggerService.configure({\n   *   minLevel: LogLevel.INFO,\n   *   enableStackTrace: false\n   * });\n */
    static configure(newConfig: Partial<LoggerConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }

    /**
     * Définir le niveau minimum de log
     * 
     * @param level Nouveau niveau minimum
     * 
     * @example\n * LoggerService.setMinLevel(LogLevel.WARN); // Seul WARN et ERROR seront affichés\n */
    static setMinLevel(level: LogLevel): void {
        this.config.minLevel = level;
    }

    /**
     * Activer ou désactiver le Logger
     * 
     * @param enabled État d'activation
     */
    static setEnabled(enabled: boolean): void {
        this.config.enabled = enabled;
    }

    /**
     * Activer ou désactiver l'affichage des stack traces
     * 
     * @param enableStackTrace État d'activation des stack traces
     */
    static setStackTraceEnabled(enableStackTrace: boolean): void {
        this.config.enableStackTrace = enableStackTrace;
    }

    /**
     * Définir le préfixe pour les logs
     * 
     * @param prefix Nouveau préfixe
     */
    static setPrefix(prefix: string): void {
        this.config.prefix = prefix;
    }

    // ==========================================================================
    // MÉTHODES D'INFORMATION
    // ==========================================================================

    /**
     * Vérifier si le Logger est activé
     * 
     * @returns true si le Logger est activé
     */
    static isEnabled(): boolean {
        return this.config.enabled;
    }

    /**
     * Obtenir le niveau minimum de log actuel
     * 
     * @returns Niveau minimum de log
     */
    static getMinLevel(): LogLevel {
        return this.config.minLevel;
    }

    /**
     * Obtenir la configuration actuelle du Logger
     * 
     * @returns Copie de la configuration actuelle
     */
    static getConfig(): LoggerConfig {
        return { ...this.config };
    }
}